import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { persistWithReceipt, PREVIEW_ONLY } from './receipts.js';

export const SILENT_PREFERENCES = Object.freeze({ completionChime: false, paydayFanfare: false,
  villageAmbience: false, kioskSound: false, softHaptic: false });
const normalize = value => Object.fromEntries(Object.keys(SILENT_PREFERENCES).map(key => [key, value?.[key] === true]));
const noop = () => {};
const FeedbackContext = createContext({ preferences: SILENT_PREFERENCES, canEdit: false,
  busy: false, message: '', availability: 'ready', assetsAvailable: false, setPreference: noop, kindle: noop,
  payday: noop, startAmbience: noop, stopAmbience: noop });
/** #577: said while the household's saved choices could not be read. Every sound stays off. */
export const PREFERENCES_UNAVAILABLE = 'We couldn’t load your sound preferences, so every sound stays off.';
export const PREFERENCES_LOADING = 'Loading your sound preferences…';

/** Asset seam: assets.{completionChime,paydayFanfare,villageAmbience} are host-supplied audio URLs.
 * No finished audio ships here. Missing assets make no request. No mount effect plays audio.
 * onSave(familyId, preferences, previous) persists with parent authority and resolves
 * {receiptId,status:'saved',familyId,preferences}. Never infer authority from a stored preference.
 *
 * #577: `availability` is the host's word for the household read — 'ready', 'loading' or
 * 'unavailable'. Anything but 'ready' is silence and no switch to flip. A later
 * `initialPreferences` (a reload, a fresh board) is adopted by VALUE: an equal answer changes
 * nothing, so a board refresh cannot stop a loop somebody started; a different answer takes effect,
 * and turning a sound off stops it.
 */
export function FeedbackProvider({ sessionKey, ...props }) {
  const identity = `${props.actor?.familyId || ''}:${props.actor?.id || ''}:${props.actor?.kind || ''}`;
  // `sessionKey` lets the host keep one subtree across the first sign-in answer (ActorContext.jsx).
  return <FeedbackSession key={sessionKey ?? identity} {...props} />;
}
function FeedbackSession({ children, actor, initialPreferences, onSave, assets = {}, availability = 'ready' }) {
  const ready = availability === 'ready' && !!actor?.familyId;
  const incoming = ready ? JSON.stringify(normalize(initialPreferences)) : '';
  const [preferences, setPreferences] = useState(() => incoming ? JSON.parse(incoming) : SILENT_PREFERENCES);
  const current = useRef(preferences);
  current.current = preferences;
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const locked = useRef(false);
  const alive = useRef(true);
  const seen = useRef(new Set());
  const media = useRef(new Set());
  const ambience = useRef(null);
  // A session actor is a grown-up, but not every grown-up is a parent: `isAdmin: false` is a member.
  const canEdit = ready && actor?.kind === 'parent' && actor?.isAdmin !== false;
  // A fresh server answer may equal the initial read but differ from a locally saved choice.
  // Compare with the current choice so equal refreshes preserve any playing ambience.
  useEffect(() => {
    const next = incoming ? JSON.parse(incoming) : SILENT_PREFERENCES;
    if (JSON.stringify(current.current) !== JSON.stringify(next)) setPreferences(next);
  }, [incoming, initialPreferences]);
  function stopAmbience() {
    if (ambience.current) { ambience.current.pause(); media.current.delete(ambience.current); ambience.current = null; }
  }
  useEffect(() => {
    alive.current = true;
    // `media` holds one Set for the provider's whole life and is never reassigned, so this is the
    // same Set the cleanup would read; the rule's DOM-node concern does not apply.
    const playing = media.current;
    return () => { alive.current = false; playing.forEach(audio => audio.pause()); playing.clear(); };
  }, []);
  // Silence takes effect immediately after a saved preference change; turning on never autoplays.
  useEffect(() => {
    media.current.forEach(audio => audio.pause()); media.current.clear(); ambience.current = null;
  }, [preferences]);
  async function setPreference(key, value) {
    if (!canEdit || locked.current || !(key in SILENT_PREFERENCES)) return;
    locked.current = true; setBusy(true);
    const next = { ...preferences, [key]: value === true };
    try {
      const receipt = await persistWithReceipt(onSave, [actor.familyId, next, preferences], 'saved', r =>
        r.familyId === actor.familyId && r.preferences?.[key] === next[key]
          && Object.keys(next).every(k => typeof r.preferences?.[k] === 'boolean'));
      if (!alive.current) return;
      if (!receipt) { setMessage(PREVIEW_ONLY); return; }
      setPreferences(normalize(receipt.preferences)); setMessage('Sound and haptic preferences saved.');
    } catch { if (alive.current) setMessage('We couldn’t save your preference. The previous choice is unchanged.'); }
    finally { locked.current = false; if (alive.current) setBusy(false); }
  }
  function play(kind, surface = 'app', loop = false) {
    if (!alive.current || !['app', 'kiosk'].includes(surface) || !current.current[kind] || (surface === 'kiosk' && !current.current.kioskSound)) return;
    if (typeof assets[kind] !== 'string' || !assets[kind] || typeof Audio !== 'function') return;
    try {
      const audio = new Audio(assets[kind]);
      audio.volume = kind === 'villageAmbience' ? .12 : .25; audio.loop = loop;
      media.current.add(audio);
      audio.onended = () => media.current.delete(audio);
      // Browsers may decline playback; no retry or sound is needed to confirm an action.
      Promise.resolve(audio.play()).catch(() => { audio.pause(); media.current.delete(audio); });
      return audio;
    } catch { return undefined; }
  }
  function kindle(receipt, surface = 'app') {
    if (receipt?.preview || !receipt?.executionId || !['pending', 'done'].includes(receipt.status) || !['app', 'kiosk'].includes(surface)) return;
    const key = `kindle:${receipt.executionId}`;
    if (seen.current.has(key)) return;
    seen.current.add(key);
    play('completionChime', surface);
    if (alive.current && surface === 'app' && current.current.softHaptic) {
      try { if (typeof navigator?.vibrate === 'function') navigator.vibrate(12); } catch { /* Unsupported or declined: silence. */ }
    }
  }
  function payday(receipt, surface = 'app') {
    if (receipt?.preview || !receipt?.receiptId || receipt.status !== 'settled') return;
    const key = `payday:${receipt.receiptId}`;
    if (seen.current.has(key)) return;
    seen.current.add(key); play('paydayFanfare', surface);
  }
  function startAmbience(surface = 'app') {
    stopAmbience(); ambience.current = play('villageAmbience', surface, true) || null;
  }
  return <FeedbackContext.Provider value={{ preferences, canEdit, busy, message, setPreference, availability,
    assetsAvailable: ['completionChime', 'paydayFanfare', 'villageAmbience'].every(key => !!assets[key]),
    ambienceAvailable: !!assets.villageAmbience, kindle, payday, startAmbience, stopAmbience }}>
    {children}
  </FeedbackContext.Provider>;
}
export function useFeedback() { return useContext(FeedbackContext); }
